import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useRef,
  useEffect,
  SetStateAction,
} from "react";
import DatePicker, { Calendar, DateObject } from "react-multi-date-picker";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { storeCounselling } from "../../store/ProgramCounselling";
import {
  ICounsellingSessionForm,
  IStoreCounselling,
  PopupContentProps,
} from "../../interfaces/CounsellingSlot";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import moment from "moment";
import { useTranslation } from "react-i18next";


const today = new Date();
const tomorrow = new Date();

type HandleDateChangeType = (
  selectedDates: DateObject | DateObject[] | null,
  options: {
    validatedValue: string | string[];
    input: HTMLElement;
    isTyping: boolean;
  }
) => false | void;

const CounsellingSessionsAddPopup: React.FC<PopupContentProps> = ({
  closePopup,
  programId,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Date[]>([]);
  const [startTime, setStartTime] = useState<Date | null>(moment().set({hours: 9, minutes: 0}).toDate()); // January 1, 1900, 09:00:00
  const [endTime, setEndTime] = useState<Date | null>(moment().set({hours: 9, minutes: 15}).toDate()); // January 1, 1900, 09:00:00
  const defaultStartTime = new Date().setHours(9, 0, 0, 0);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [zoom, setZoom] = useState("");


  const handleDateChange: HandleDateChangeType = (selectedDates, options) => {
    // Handle the date change and update the state
    const convertedDates = selectedDates
      ? Array.isArray(selectedDates)
        ? selectedDates.map((dateObject) => dateObject.toDate())
        : [selectedDates.toDate()]
      : [];

    setValues(convertedDates);
  };

  const dispatch = useDispatch<any>();
  const [counselling, setCounselling] = useState<ICounsellingSessionForm[]>([]);

  const handleStartTimeChange = (
    timeSelected: DateObject | DateObject[] | null
  ) => {
    if (timeSelected) {      
      setStartTime(moment(timeSelected?.toString(), ["HH:mm", "hh:mm a", "hh:mm A"]).toDate());      
    }
  };

  const handleEndTimeChange = (
    timeSelected: DateObject | DateObject[] | null
  ) => {
    if (timeSelected) {
        setEndTime(moment(timeSelected?.toString(), ["HH:mm", "hh:mm a", "hh:mm A"]).toDate());
      // } else {
      //   setEndTime(startTime);
      // }
      
    }    
  };

  const handlezoomChange = (e: ChangeEvent<HTMLInputElement>) => {
    setZoom(e.target.value);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedDuration) {
      toast.warning(t("adminPrograms.counsellingForm.selectDurationWarning", "Please select a duration"));
      return;
    }

    if (!values || values.length === 0) {
      toast.warning(t("adminPrograms.counsellingForm.selectDateWarning", "Please select at least one date"));
      return;
    }

    // Create a new counselling session object
    const newCounsellingSession: ICounsellingSessionForm = {
      programId: programId ?? "0",
      startTime,
      endTime,
      duration: selectedDuration || 0,
      selectedDates: values,
      zoomLink: zoom,
    };
 
    dispatch(storeCounselling(newCounsellingSession))
      .then(() => {
        toast.success(t("adminPrograms.counsellingForm.storedSuccess", "Counselling slot added successfully"));
        closePopup();
      })
      .catch((err: { message: any }) => {
        if (err instanceof Error) {
          console.error(err.message);
        }
      });
  };

  return (
    <div className="popup-overlay relative z-[100] overflow-auto my-8">
      <div className="relative  bg-white px-2 md:px-8 w-80 md:w-[32rem] rounded-lg text-left overflow-auto">
        <button onClick={closePopup} className="close-btn">
          &times;
        </button>
        <div className="max-w-md mt-2 m-auto p-4 pb-4 md:p-8 bg-white rounded ">
          <h2 className="text-xl md:text-2xl flex font-gilroy mb-4 font-bold  text-[#263238]">
            <img
              src="./images/round_icon.png"
              alt="condition"
              className="object-contain pr-2"
            />
            {t("adminPrograms.counsellingForm.addTitle", "Add New Counselling")}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="mb-4 mt-2">
              <label
                className="block text-[#607D8B] text-sm mb-1  font-gilroy"
                htmlFor="title"
              >
                {t("adminPrograms.counsellingForm.startTime", "Start Time")}
              </label>
              <DatePicker
                disableDayPicker
                format="HH:mm"
                name="start-time"
                placeholder={t("adminPrograms.counsellingForm.startTimePlaceholder", "Please add start time")}
                onChange={(dateTime) => handleStartTimeChange(dateTime)}
                plugins={[<TimePicker  mStep={15} hideSeconds/>]}
                value={startTime}

              />
            </div>

            <div className="mb-4 mt-2">
              <label
                className="block text-[#607D8B] text-sm mb-1  font-gilroy"
                htmlFor="title"
              >
                {t("adminPrograms.counsellingForm.endTime", "End Time")}
              </label>
              <DatePicker
                disableDayPicker
                format="HH:mm"
                name="end-time"
                placeholder={t("adminPrograms.counsellingForm.endTimePlaceholder", "Please add end time")}
                onChange={(dateTime) => handleEndTimeChange(dateTime)}
                plugins={[<TimePicker mStep={15} hideSeconds/>]}
                value={endTime}
              />
            </div>

            <div className="mb-4 mt-2">
              <label
                className="block text-[#607D8B] text-sm mb-1  font-gilroy"
                htmlFor="title"
              >
                {t("adminPrograms.counsellingForm.duration", "Duration")}
              </label>
              <select
                id="duration"
                title="Duration"
                className="w-full font-gilroy border-2 border-[#c0c4d6] px-3 py-3 rounded-lg focus:outline-none bg-[#EDF2F6]"
                value={selectedDuration || ""}
                onChange={(e) =>
                  setSelectedDuration(parseInt(e.target.value, 10) || null)
                }
                required
              >
                <option value="">{t("adminPrograms.counsellingForm.selectDuration", "Select Hours")}</option>
                <option value="15">{t("adminPrograms.counsellingForm.duration15", "15 Min")}</option>
                <option value="30">{t("adminPrograms.counsellingForm.duration30", "30 Min")}</option>
                <option value="45">{t("adminPrograms.counsellingForm.duration45", "45 Min")}</option>
                <option value="60">{t("adminPrograms.counsellingForm.duration60", "1 hour")}</option>
               
              </select>
            </div>

            <div className="mb-4 mt-2">
              <label
                className="block text-[#607D8B] text-sm mb-2"
                htmlFor="date"
              >
                {t("adminPrograms.counsellingForm.selectDates", "Select Dates")}
              </label>
              <Calendar
                multiple
                minDate={new Date()}
                highlightToday={false}
                onChange={
                  handleDateChange as unknown as (
                    selectedDates: DateObject | DateObject[] | null
                  ) => false | void
                }
                value={values}
              />
            </div>
            <div className="mb-4 w-full  pr-4">
              <label
                  className="block text-[#607D8B] text-sm  mb-2"
                  htmlFor="title"
              >
                {t("adminPrograms.counsellingForm.zoomLink", "Zoom link")}
              </label>
              <input
                  type="text"
                  id="zoom"
                  name="zoom"
                  value={zoom}
                  pattern="https?://.+"
                  onChange={handlezoomChange}
                  className="border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
                  placeholder={t("adminPrograms.counsellingForm.zoomLinkPlaceholder", "Enter a zoom link")}
              />
            </div>

            <button
              type="submit"
              className="bg-[#B4804A] font-gilroy w-full text-white py-2 px-4 font-semibold rounded-md focus:outline-none border-[#E7EFF3] border-2 focus:border-gray-500"
            >
              {t("adminPrograms.counsellingForm.save", "Save")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CounsellingSessionsAddPopup;
